import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMutation } from '@apollo/client/react';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import moment from 'moment/moment';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import { GridViewMenuItem } from '@components-clean/core/grid/grid-menu/grid-view-menu';
import Grid from '@components-clean/core/grid-fe';
import { YesNoCell, DateCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { AddDataViewDialog } from '@pages-clean/admin/study/admin-data-views/data-views/dialogs/add-data-view-dialog';
import {
    DataViewConfig,
    DataViewConfigForm,
    PanelData,
} from '@pages-clean/admin/study/admin-data-views/data-views/types';
import { DataFilterResultType } from '@pages-clean/admin/study/admin-data-views/filters/types';
import { DataViewConfigValidation } from 'types/graphql-global-types';
import { useTranslation, useLocalState } from '@hooks/index';
import Constants, { GridViewOptions } from '@utils/constants';
import NotificationService from '@utils/notification';
import { GET_DATA_VIEW_CONFIGURATION_NAMES, SAVE_DATA_VIEW_DISPLAY_ORDER } from '@queries/central-monitoring';
import {
    dataReviewTables,
    addDataViewConfig_OpraMutations_addDataViewConfig_DataViewConfigurationResult,
    updateDataViewDisplayOrder,
} from '@queries/generated/central-monitoring.generated';
import { StudyFields } from '@queries/generated/fragments.generated';

type DataViewListProps = {
    onDataViewChange: (dvDetails: DataViewConfig | undefined) => void;
    dataViewConfigData: DataViewConfig[];
    study: StudyFields;
    onDataViewConfigAdd: (data: addDataViewConfig_OpraMutations_addDataViewConfig_DataViewConfigurationResult) => void;
    panelsData: PanelData[];
    dataTablesData?: dataReviewTables;
    filtersData: DataFilterResultType[];
    gridViewOptions?: GridViewMenuItem[];
    isEditMode: boolean;
    setIsEditMode: Dispatch<SetStateAction<boolean>>;
    canEdit: boolean;
    setGridCurrentView: Dispatch<SetStateAction<GridViewOptions>>;
    isReorderMode: boolean;
    setIsReorderMode: Dispatch<SetStateAction<boolean>>;
    gridCurrentView: GridViewOptions;
    gridFilterListener: (gridFilter?: CompositeFilterDescriptor) => void;
    gridFilter?: CompositeFilterDescriptor;
    selectedItem?: DataViewConfig;
};

const COMPONENT_NAME = 'data-view-list';

const DataViewList = ({
    onDataViewChange,
    dataViewConfigData,
    study,
    onDataViewConfigAdd,
    panelsData,
    dataTablesData,
    filtersData,
    gridViewOptions,
    isEditMode,
    setIsEditMode,
    canEdit,
    setGridCurrentView,
    isReorderMode,
    setIsReorderMode,
    gridCurrentView,
    gridFilterListener,
    gridFilter,
    selectedItem,
}: DataViewListProps) => {
    const { t } = useTranslation();
    const { setReadOnly } = useReadOnlyContext();
    const [reOrderedData, setReOrderedData] = useState<DataViewConfig[] | undefined>(dataViewConfigData);
    const [saveDataViewOrder] = useMutation<updateDataViewDisplayOrder>(SAVE_DATA_VIEW_DISPLAY_ORDER);
    const [defaultSelectedItem, setDefaultSelectedItem] = useState<DataViewConfig | undefined>(selectedItem);
    const { state } = useLocalState();
    const [reorderIsDirty, setReorderIsDirty] = useState<boolean>(false);
    const fieldDefinitionFields = state?.fields?.filter((field) => !field?.isHidden);
    const {
        setComponentHasChanges,
        componentHasChanges,
        confirmDiscardChanges,
        confirmDraggableChangesWillClearFilters,
    } = useUnsavedChangesContext();

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setDefaultSelectedItem(selectedItem);
    }, [selectedItem]);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setReOrderedData(
            dataViewConfigData
                .map((item) => {
                    return {
                        updatedByFullName: `${item?.updatedBy?.firstName} ${item?.updatedBy?.lastName}`,
                        createdByFullName: `${item?.createdBy?.firstName} ${item?.createdBy?.lastName}`,
                        mainPanelName: panelsData.find((panel) => panel?.id === item.mainPanelId)?.title,
                        panels: item?.panelIds
                            .map((panelId) => {
                                return panelsData.find((panel) => panelId === panel?.id)?.title;
                            })
                            .join(', '),
                        dataTables: item?.dataTableIds
                            .map((dataTableId) => {
                                return dataTablesData?.dataReviewTablesForStudy?.find((dt) => dataTableId === dt?.id)
                                    ?.name;
                            })
                            .join(', '),
                        filters: item?.dataFilterIds
                            .map((filterId) => {
                                const filter = filtersData.find((df) => filterId === df?.id);
                                return `Label:${filter?.label} Field:${filter?.fieldKey}`;
                            })
                            .join(', '),
                        dataSourceKey: Constants.DataSourceLOV.find((ds) => ds.code === item.dataSource)?.key,
                        ...item,
                        mainPanelPlotTitle: panelsData?.find((panel) => panel?.id === item.mainPanelId)?.title,
                        isMainPanelActive: panelsData?.find((panel) => panel?.id === item.mainPanelId)?.active,
                        isAnyPanelInactive:
                            panelsData?.filter(
                                (panel) => item.panelIds.includes(String(panel?.id)) && panel?.active === false,
                            ).length === 0,
                        isAnyDataTableInactive:
                            dataTablesData?.dataReviewTablesForStudy?.filter(
                                (dt) => item.dataTableIds.includes(String(dt?.id)) && dt?.active === false,
                            ).length === 0,
                        isAnyFieldInActiveOrDeleted:
                            fieldDefinitionFields?.filter((field) =>
                                filtersData
                                    ?.filter((dt) => item.dataFilterIds.includes(String(dt?.id)))
                                    .map((filter) => filter.fieldKey)
                                    .includes(String(field?.key)),
                            ).length === item.dataFilterIds.length,
                    };
                })
                .sort((a, b) => a.displayIndex - b.displayIndex) || [],
        );
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [dataViewConfigData, panelsData, filtersData]);

    const {
        formState: { isDirty },
        reset,
    } = useFormContext<DataViewConfigForm>();

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, (reorderIsDirty && isReorderMode) || (isEditMode && isDirty));
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [reorderIsDirty, isReorderMode, isDirty, isEditMode]);

    const defineOrder = (configs: DataViewConfig[]) =>
        configs.map((config, index) => ({ ...config, displayIndex: index + 1 }));

    const saveRowOrder = (data: DataViewConfig[]) => {
        saveDataViewOrder({
            variables: {
                studyId: study.id as string,
                configIds: data.map((item) => item.id),
            },
            refetchQueries: [
                {
                    query: GET_DATA_VIEW_CONFIGURATION_NAMES,
                    variables: { studyId: study.id as string },
                },
            ],
        })
            .then(() => {
                NotificationService.showNotification({ text: t('actionsSuccessfulToast'), type: 'success' });
                setReOrderedData(defineOrder(data));
            })
            .catch((err) => {
                window.console.error(err);
            });
    };

    const handleAddDataViewResponse = (
        resp?: addDataViewConfig_OpraMutations_addDataViewConfig_DataViewConfigurationResult | null,
    ) => {
        if (resp) {
            onDataViewConfigAdd(resp); // Updates or reloads data
            if (resp.result) {
                NotificationService.showNotification({ text: t('actionsSuccessfulToast'), type: 'success' });
                setDefaultSelectedItem(resp.result as DataViewConfig);
                setIsEditMode(true);
            }
        }
    };

    const { TooltipCell } = createGridCustomCells();

    const dataViewColumns: GridColumnProps[] = [
        {
            id: 'name',
            title: t('admin:dataViewName'),
            field: 'name',
            filter: 'text',
            locked: true,
            lockedByDefault: true,
            minWidth: 185,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'isActive',
            title: t('admin:dataViewActive'),
            field: 'isActive',
            filter: 'boolean',
            minWidth: 78,
            cells: { data: YesNoCell },
        },
        {
            id: 'mainPanelPlotTitle',
            title: t('admin:dataViewMainPanel'),
            field: 'mainPanelPlotTitle',
            filter: 'text',
            minWidth: 180,
            activeField: 'isMainPanelActive',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                    activeField: 'isMainPanelActive',
                }),
            },
        },
        {
            id: 'dataSourceKey',
            title: t('admin:dataViewDataSource'),
            field: 'dataSourceKey',
            filter: 'text',
            minWidth: 180,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'panels',
            title: t('admin:panels'),
            field: 'panels',
            filter: 'text',
            activeField: 'isAnyPanelInactive',
            minWidth: 280,
            inactiveIconTooltip: t('common:multipleRecordsInactiveIcon'),
            cells: {
                data: TooltipCell({
                    isCsv: true,
                    activeField: 'isAnyPanelInactive',
                    inactiveIconTooltip: t('common:multipleRecordsInactiveIcon'),
                }),
            },
        },
        {
            id: 'dataTables',
            title: t('admin:dataTables'),
            field: 'dataTables',
            filter: 'text',
            activeField: 'isAnyDataTableInactive',
            minWidth: 280,
            inactiveIconTooltip: t('common:multipleRecordsInactiveIcon'),
            cells: {
                data: TooltipCell({
                    isCsv: true,
                    activeField: 'isAnyDataTableInactive',
                    inactiveIconTooltip: t('common:multipleRecordsInactiveIcon'),
                }),
            },
        },
        {
            id: 'filters',
            title: t('admin:dataViewFilters'),
            field: 'filters',
            filter: 'text',
            minWidth: 280,
            activeField: 'isAnyFieldInActiveOrDeleted',
            inactiveIconTooltip: t('admin:atLeastOneFieldNoLongerInUse'),
            cells: {
                data: TooltipCell({
                    isCsv: true,
                    activeField: 'isAnyFieldInActiveOrDeleted',
                    inactiveIconTooltip: t('common:multipleRecordsInactiveIcon'),
                }),
            },
        },
        {
            id: 'createdByFullName',
            title: t('metadataColumns.createdBy'),
            field: 'createdByFullName',
            filter: 'text',
            showByDefault: false,
            minWidth: 140,
        },
        {
            id: 'created',
            title: t('metadataColumns.createdDate'),
            field: 'created',
            filter: 'date',
            showByDefault: false,
            cells: {
                data: DateCell,
            },
            cellName: 'DateCell',
            minWidth: 145,
        },
        {
            id: 'updatedByFullName',
            title: t('metadataColumns.updatedBy'),
            field: 'updatedByFullName',
            filter: 'text',
            showByDefault: false,
            minWidth: 140,
        },
        {
            id: 'updated',
            title: t('metadataColumns.updatedDate'),
            field: 'updated',
            filter: 'date',
            showByDefault: false,
            cells: {
                data: DateCell,
            },
            cellName: 'DateCell',
            minWidth: 145,
        },
        {
            id: 'configId',
            title: t('configId'),
            field: 'configId',
            filter: 'text',
            showByDefault: false,
            minWidth: 175,
        },
        {
            id: 'recordId',
            title: t('recordId'),
            field: 'recordId',
            filter: 'numeric',
            showByDefault: false,
            minWidth: 105,
        },
    ];

    const confirmReorder = async (onSubmit: () => void, onClose: () => void) => {
        const confirmed = await confirmDraggableChangesWillClearFilters(
            t('admin:dataView.editDataViewsOrderDialogTitle'),
            t('admin:editGridRowOrderDialog'),
            'data-view-reorder-data-warning',
        );
        setReadOnly(false);
        if (!confirmed) {
            onClose();
        } else {
            onSubmit();
            setIsReorderMode(true);
        }
    };

    return (
        <div className="k-align-items-end k-h-full">
            <div className="k-h-full">
                <Grid
                    filterListener={(newFilter) => {
                        gridFilterListener && gridFilterListener(newFilter);
                    }}
                    showPlaceholder={false}
                    filter={gridFilter}
                    title={isReorderMode ? t('admin:dataView.editDataViewsOrderGridTitle') : t('admin:dataViews')}
                    canEdit={canEdit}
                    data={reOrderedData}
                    className="k-h-full'"
                    gridViewOptions={gridViewOptions}
                    maxHeight="auto"
                    testDataId="data-view-list"
                    columns={dataViewColumns}
                    sortable={{ mode: 'single', allowUnsort: true }}
                    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                    // eslint-disable-next-line @eslint-react/purity
                    exportFilename={`DataViews_${study.studyNumber}_${moment(new Date()).format('DD-MMM-yyyy')}`}
                    dataItemKey="recordId"
                    selectable={{ enabled: true, mode: 'single' }}
                    preSelectionChange={async (event) => {
                        if (componentHasChanges(COMPONENT_NAME)) {
                            const confirmed = await confirmDiscardChanges('edit-data-view-confirmation');
                            if (!confirmed) {
                                return false;
                            }
                        }
                        reset();
                        setIsEditMode(false);
                        setDefaultSelectedItem(event.startDataItem as DataViewConfig);
                        return true;
                    }}
                    onSelectionChange={(event) => {
                        onDataViewChange(event.startDataItem as DataViewConfig);
                    }}
                    defaultSelectedItem={defaultSelectedItem}
                    rowReorder
                    saveReorder={(data) => {
                        saveRowOrder(data as DataViewConfig[]);
                        setGridCurrentView(gridCurrentView);
                        setIsReorderMode(false);
                        setReorderIsDirty(false);
                        return true;
                    }}
                    cancelReorder={() => {
                        setGridCurrentView(gridCurrentView);
                        setIsReorderMode(false);
                        setReorderIsDirty(false);
                        return true;
                    }}
                    setReorderIsDirty={() => {
                        setReorderIsDirty(true);
                    }}
                    setReorderIsActive={() => {
                        setIsReorderMode(true);
                    }}
                    reorderConfirmation={async (onSubmit, onClose) => {
                        setReadOnly(true);
                        if (componentHasChanges(COMPONENT_NAME)) {
                            const confirmed = await confirmDiscardChanges('data-view-reorder-confirmation');
                            if (!confirmed) {
                                onClose();
                            } else {
                                setComponentHasChanges(COMPONENT_NAME, false);
                                reset();
                                setIsEditMode(false);
                                await confirmReorder(onSubmit, onClose);
                            }
                        } else {
                            await confirmReorder(onSubmit, onClose);
                        }
                    }}
                    setEditMode={() => setIsEditMode && setIsEditMode(false)}
                    resetChanges={() => reset && reset()}
                    addItemDialog={(onSubmit, onClose) => {
                        return (
                            <AddDataViewDialog
                                studyRef={study.id as string}
                                onClose={onClose}
                                existingViews={reOrderedData ?? []}
                                panels={panelsData}
                                handleSubmitResponse={(event) => {
                                    if (event?.validation === DataViewConfigValidation.SUCCESS) onSubmit(true, false);
                                    handleAddDataViewResponse(event);
                                }}
                            />
                        );
                    }}
                />
            </div>
        </div>
    );
};
export default DataViewList;
